import { formatError, isErrorKey, type ErrorKey, type FeedbackInput, type Locale } from '@chess-uno/shared-protocol'
import { loadSession, SERVER_URL } from './session.js'

export type FeedbackPayload = Pick<FeedbackInput, 'kind' | 'rating' | 'message' | 'contact' | 'gameId'> & {
  /** Honeypot: people never see this field. */
  website?: string
}

export type FeedbackResult = { ok: true } | { ok: false; key: ErrorKey }

/**
 * Sends feedback from wherever the visitor is. Signed-in players are
 * recognized by their token; the page, language and screen size come along
 * so a "bug" report is understandable without a back-and-forth.
 */
export async function sendFeedback(payload: FeedbackPayload, locale: Locale): Promise<FeedbackResult> {
  const session = loadSession()
  let res: Response
  try {
    res = await fetch(`${SERVER_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(session ? { authorization: `Bearer ${session.token}` } : {}) },
      body: JSON.stringify({
        ...payload,
        page: window.location.pathname,
        locale,
        viewport: window.innerWidth < 760 ? 'mobile' : 'desktop',
      }),
    })
  } catch {
    return { ok: false, key: 'server_unreachable' }
  }
  if (res.ok) return { ok: true }
  const body = (await res.json().catch(() => ({}))) as { key?: unknown }
  return { ok: false, key: isErrorKey(body.key) ? body.key : 'internal' }
}

export function feedbackErrorText(key: ErrorKey, locale: Locale): string {
  return formatError(key, locale)
}

// ---------------------------------------------------------------------------
// When to ask for a score after a game
// ---------------------------------------------------------------------------

const MEMORY_KEY = 'chess-uno:feedback'

/** What this browser remembers about the questions it asked. */
export interface FeedbackMemory {
  /** Games seen to the end. */
  games: number
  /** Number of the game after which a score was last asked. */
  lastAskedAt: number
  /** Number of the game after which a score was last given (0 = never). */
  lastAnsweredAt: number
  /** The game the last decision was made for (the answer stays the same for it). */
  lastGameId: string | null
  lastAsked: boolean
}

export const EMPTY_MEMORY: FeedbackMemory = { games: 0, lastAskedAt: 0, lastAnsweredAt: 0, lastGameId: null, lastAsked: false }

/** Games to let pass before asking again: soon after an ignored question, rarely after an answer. */
export const ASK_AGAIN_AFTER = { ignored: 3, answered: 10 }

/**
 * Decides whether the score question shows after this game. It is asked after
 * the first game, then only now and then; never twice for the same game, and
 * much less often to someone who already answered.
 */
export function decideAsk(memory: FeedbackMemory, gameId: string): { ask: boolean; memory: FeedbackMemory } {
  if (memory.lastGameId === gameId) return { ask: memory.lastAsked, memory }
  const games = memory.games + 1
  const since = games - Math.max(memory.lastAskedAt, memory.lastAnsweredAt)
  const wait = memory.lastAnsweredAt > 0 ? ASK_AGAIN_AFTER.answered : ASK_AGAIN_AFTER.ignored
  const ask = memory.lastAskedAt === 0 || since >= wait
  return { ask, memory: { ...memory, games, lastGameId: gameId, lastAsked: ask, lastAskedAt: ask ? games : memory.lastAskedAt } }
}

function readMemory(): FeedbackMemory {
  try {
    const raw = localStorage.getItem(MEMORY_KEY)
    return raw ? { ...EMPTY_MEMORY, ...(JSON.parse(raw) as Partial<FeedbackMemory>) } : EMPTY_MEMORY
  } catch {
    return EMPTY_MEMORY
  }
}

function writeMemory(memory: FeedbackMemory): void {
  try {
    localStorage.setItem(MEMORY_KEY, JSON.stringify(memory))
  } catch {
    // Storage blocked: the question may come back a little more often, nothing else.
  }
}

/** Remembers the game and tells whether to ask for a score after it. */
export function shouldAskAfterGame(gameId: string): boolean {
  const { ask, memory } = decideAsk(readMemory(), gameId)
  writeMemory(memory)
  return ask
}

export function rememberAnswered(): void {
  const memory = readMemory()
  writeMemory({ ...memory, lastAnsweredAt: memory.games })
}
